import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../../components/layout/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { FiEdit2, FiTrash2, FiUserX, FiSearch } from "react-icons/fi"; 
import FormDataGuru from "./FormDataGuru";
import ConfirmDeleteModal from "../../components/modal/Delete";

export default function DataGuru() {
  const { token } = useAuth();
  const navigate  = useNavigate();
  const API       = "https://backend.smpn1kamal.sch.id/api";

  const [guru,           setGuru]           = useState([]);
  const [search,         setSearch]         = useState("");
  const [openForm,       setOpenForm]       = useState(false);
  const [editData,       setEditData]       = useState(null);
  const [openDelete,     setOpenDelete]     = useState(false);
  const [selectedId,     setSelectedId]     = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [guruAbsenCount, setGuruAbsenCount] = useState(0);

  const fetchGuru = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/data-guru`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGuru(res.data);
    } catch {
      toast.error("Gagal mengambil data guru");
    }
  }, [token]);

  const fetchGuruAbsenCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pengganti/kosong-semua`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: "kosong" },
      });
      setGuruAbsenCount(res.data?.data?.length || 0);
    } catch {
      // silent
    }
  }, [token]);

  useEffect(() => {
    fetchGuru();
    fetchGuruAbsenCount();
  }, [fetchGuru, fetchGuruAbsenCount]);

  const handleDelete = async () => {
    try {
      setLoadingDelete(true);
      await axios.delete(`${API}/data-guru/${selectedId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Data guru berhasil dihapus");
      setOpenDelete(false);
      fetchGuru();
    } catch {
      toast.error("Gagal menghapus data guru");
    } finally {
      setLoadingDelete(false);
    }
  };

  const filteredGuru = guru.filter(
    (g) =>
      g.nama_lengkap.toLowerCase().includes(search.toLowerCase()) ||
      (g.nip || "").toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenForm = (data = null) => {
    setEditData(data);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    fetchGuru();
  };

  return (
    <AdminLayout>
      <div className="space-y-6">

        {/* HEADER DESKTOP */}
        <div className="hidden md:flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-indigo-600">Data Guru</h1>
            <p className="text-sm text-gray-500">SMPN 1 Kamal</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/admin/guru-absen")}
              className="relative flex items-center gap-2 bg-red-50 text-red-600 hover:bg-red-100 px-5 py-2.5 rounded-full text-sm font-semibold border border-red-100 transition shadow-sm"
            >
              <FiUserX size={16} />
              Guru Tidak Hadir
              {guruAbsenCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white ring-2 ring-white shadow-sm">
                  {guruAbsenCount > 9 ? "9+" : guruAbsenCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* HEADER MOBILE */}
        <div className="md:hidden flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-indigo-600">Data Guru</h1>
            <p className="text-sm text-gray-500">SMPN 1 Kamal</p>
          </div>
          <button
            onClick={() => navigate("/admin/guru-absen")}
            className="relative p-3 bg-red-50 text-red-600 rounded-full border border-red-100"
          >
            <FiUserX size={18} />
            {guruAbsenCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white ring-1 ring-white">
                {guruAbsenCount > 9 ? "9+" : guruAbsenCount}
              </span>
            )}
          </button>
        </div>

        {/* TABEL / KONTEN UTAMA */}
        <div className="bg-white rounded-2xl shadow-md p-5">
          <div className="flex justify-between items-center mb-5 gap-3">
            <p className="text-sm text-gray-500 hidden sm:block">
              Total: <span className="font-semibold text-gray-700">{filteredGuru.length}</span> guru
            </p>
            <div className="relative w-full sm:w-72">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
              <input
                type="text"
                placeholder="Cari nama / NIP guru..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border rounded-full text-sm focus:ring-2 focus:ring-indigo-400 outline-none"
              />
            </div>
          </div>

          {/* ── MOBILE CARDS (Avatar Dihapus) ── */}
          <div className="md:hidden space-y-4">
            {filteredGuru.length === 0 ? (
              <p className="text-center py-8 text-gray-400 text-sm">Data tidak ditemukan</p>
            ) : (
              filteredGuru.map((g) => (
                <div key={g.id_guru} className="border border-gray-100 rounded-xl p-4 shadow-sm">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-800 truncate">{g.nama_lengkap}</p>
                    <p className="text-xs text-gray-400 font-mono">NIP: {g.nip || "-"}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {g.nama_mapel
                        ? g.nama_mapel.split(",").map((m, idx) => (
                            <span key={idx} className="bg-indigo-50 text-indigo-600 px-2.5 py-0.5 text-[11px] font-semibold rounded-full">
                              {m.trim()}
                            </span>
                          ))
                        : <span className="text-xs text-gray-300">-</span>
                      }
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => handleOpenForm(g)}
                      className="flex-1 flex items-center justify-center gap-1.5 bg-blue-50 text-blue-600 py-2 rounded-full text-sm hover:bg-blue-100 transition font-medium"
                    >
                      <FiEdit2 size={13} /> Edit
                    </button>
                    <button
                      onClick={() => { setSelectedId(g.id_guru); setOpenDelete(true); }}
                      className="flex-1 flex items-center justify-center gap-1.5 bg-red-50 text-red-500 py-2 rounded-full text-sm hover:bg-red-100 transition font-medium"
                    >
                      <FiTrash2 size={13} /> Hapus
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* ── DESKTOP TABLE (Avatar Dihapus) ── */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-3.5 text-left w-12">No</th>
                  <th className="px-4 py-3.5 text-left">NIP</th>
                  <th className="px-4 py-3.5 text-left">Nama Guru</th>
                  <th className="px-4 py-3.5 text-left">Mata Pelajaran</th>
                  <th className="px-4 py-3.5 text-center w-24">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredGuru.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-gray-400">
                      Data tidak ditemukan
                    </td>
                  </tr>
                ) : (
                  filteredGuru.map((g, i) => (
                    <tr key={g.id_guru} className="hover:bg-gray-50/60 transition">
                      <td className="px-4 py-3.5 text-gray-400">{i + 1}</td>
                      <td className="px-4 py-3.5 font-mono text-xs text-gray-500">{g.nip || "-"}</td>
                      <td className="px-4 py-3.5">
                        <span className="font-medium text-gray-800">{g.nama_lengkap}</span>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex flex-wrap gap-1">
                          {g.nama_mapel
                            ? g.nama_mapel.split(",").map((m, idx) => (
                                <span key={idx} className="bg-indigo-50 text-indigo-600 px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase">
                                  {m.trim()}
                                </span>
                              ))
                            : <span className="text-gray-300">-</span>
                          }
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleOpenForm(g)}
                            className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition"
                            title="Edit"
                          >
                            <FiEdit2 size={13} />
                          </button>
                          <button
                            onClick={() => { setSelectedId(g.id_guru); setOpenDelete(true); }}
                            className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 text-red-500 hover:bg-red-100 transition"
                            title="Hapus"
                          >
                            <FiTrash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* MODALS */}
        <FormDataGuru
          isOpen={openForm}
          editData={editData}
          onClose={handleCloseForm}
        />

        <ConfirmDeleteModal
          open={openDelete}
          onClose={() => setOpenDelete(false)}
          onConfirm={handleDelete}
          loading={loadingDelete}
          title="Hapus Guru"
          description="Data guru yang dihapus tidak dapat dikembalikan."
        />
      </div>
    </AdminLayout>
  );
}